export type UserRole = 'owner' | 'admin' | 'manager' | 'teacher' | 'parent';
export type UserStatus = 'active' | 'inactive' | 'pending';

export const VALID_ROLES: UserRole[] = ['owner', 'admin', 'manager', 'teacher', 'parent'];

/** Roles that have full administrative access (everything including settings) */
export const SETTINGS_ROLES: UserRole[] = ['owner', 'admin'];

/** Roles that have management-level access (everything except settings) */
export const MANAGEMENT_ROLES: UserRole[] = ['owner', 'admin', 'manager'];

/** Helper to check if a role is admin-level (owner or admin) */
export function isAdminRole(role: UserRole | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/** Helper to check if a role is management-level (owner, admin, or manager) */
export function isManagementRole(role: UserRole | undefined): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager';
}

export interface User {
  id?: string;
  email: string;
  name: string;
  displayName?: string; // optional computed display name for UI (e.g. "Student (Parent)")
  role: UserRole;
  status?: UserStatus; // User account status
  tenantId?: string;
  phoneNumber?: string;
  profilePicture?: string;
  // Teacher specific fields
  grade?: string; // Grade the teacher is assigned to (e.g., "1", "2", "KG")
  assignedClasses?: string[]; // Class IDs assigned to this teacher (should be within the same grade)
  subject?: string; // Single subject teacher teaches (e.g., "Mathematics", "Science")
  subjects?: string[];
  incharge?: boolean; // Indicates if the teacher is class incharge
  // Parent specific fields
  children?: string[]; // IDs of students for parent users
  // Platform user flag - indicates if this is a platform support user granted access
  isPlatformUser?: boolean;
  createdAt?: Date | any;
  updatedAt?: Date | any;
}

// School settings interface for platform access consent
export interface SchoolSettings {
  allowPlatformAccess?: boolean; // Whether school admin has consented to platform user access
  platformAccessEnabledAt?: Date | any;
  platformAccessEnabledBy?: string;
  updatedAt?: Date | any;
}
